/**
 * Create / edit physical PMV components (+ optional definition create).
 * Reuses POST/PATCH /pmv/components and definitions — no parallel masters.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Modal, Select, Space, Typography, message } from 'antd';
import pmvService, { type PmvComponentDefinition } from '@/services/pmv.service';

const { Text } = Typography;

const FAMILY_OPTS = [
  { value: 'TYRE', label: 'Tyre' },
  { value: 'BATTERY', label: 'Battery' },
  { value: 'GENERIC_COMPONENT', label: 'Generic' },
];

const LOCATION_OPTS = [
  'NONE',
  'WAREHOUSE',
  'YARD',
  'WORKSHOP',
  'INSTALLED',
  'SUPPLIER',
  'OTHER',
].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

const CONDITION_OPTS = [
  'NEW',
  'GOOD',
  'SERVICEABLE',
  'WORN',
  'DAMAGED',
  'REPAIRABLE',
  'RETREADABLE',
  'UNSERVICEABLE',
  'SCRAP',
].map((v) => ({ value: v, label: v }));

export type ComponentFormMode = 'create' | 'edit';

export type ComponentEditSeed = {
  id: string;
  family?: string;
  component_number?: string;
  serial_number?: string | null;
  manufacturer_serial?: string | null;
  status?: string;
  condition_code?: string | null;
  current_location_type?: string | null;
  current_location_reference?: string | null;
  ownership?: string | null;
  is_active?: boolean;
};

export const PmvComponentFormModal: React.FC<{
  open: boolean;
  mode: ComponentFormMode;
  /** Prefill family tab from register */
  defaultFamily?: string;
  edit?: ComponentEditSeed | null;
  onClose: () => void;
  onSaved?: (componentId: string) => void;
}> = ({ open, mode, defaultFamily, edit, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const [family, setFamily] = useState(defaultFamily || 'TYRE');
  const [needDefinition, setNeedDefinition] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && edit) {
      form.setFieldsValue({
        serial_number: edit.serial_number || undefined,
        manufacturer_serial: edit.manufacturer_serial || undefined,
        condition_code: edit.condition_code || undefined,
        current_location_type: edit.current_location_type || 'WAREHOUSE',
        current_location_reference: edit.current_location_reference || undefined,
        ownership: edit.ownership || undefined,
        is_active: edit.is_active !== false,
      });
      if (edit.family) setFamily(edit.family);
      setNeedDefinition(false);
    } else {
      form.resetFields();
      form.setFieldsValue({
        family: defaultFamily && defaultFamily !== 'ALL' ? defaultFamily : 'TYRE',
        current_location_type: 'WAREHOUSE',
        status: 'AVAILABLE',
        condition_code: 'NEW',
      });
      setFamily(defaultFamily && defaultFamily !== 'ALL' ? defaultFamily : 'TYRE');
      setNeedDefinition(false);
    }
  }, [open, mode, edit, defaultFamily, form]);

  const defsQ = useQuery({
    queryKey: ['pmv-component-definitions', family],
    queryFn: () => pmvService.listComponentDefinitions({ family, limit: 200 }),
    enabled: open && mode === 'create',
    staleTime: 20_000,
  });

  const defOptions = useMemo(
    () =>
      (defsQ.data || []).map((d: PmvComponentDefinition) => ({
        value: d.id,
        label: `${d.code} — ${d.name}${d.brand || d.model ? ` (${[d.brand, d.model].filter(Boolean).join(' ')})` : ''}`,
      })),
    [defsQ.data]
  );

  useEffect(() => {
    if (open && mode === 'create' && defsQ.isSuccess && defOptions.length === 0) {
      setNeedDefinition(true);
    }
  }, [open, mode, defsQ.isSuccess, defOptions.length]);

  const saveMut = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      if (mode === 'edit' && edit?.id) {
        return pmvService.patchComponent(edit.id, {
          serial_number: (values.serial_number as string) || null,
          manufacturer_serial: (values.manufacturer_serial as string) || null,
          condition_code: (values.condition_code as string) || null,
          current_location_type: values.current_location_type as string,
          current_location_reference: (values.current_location_reference as string) || null,
          ownership: (values.ownership as string) || null,
          is_active: values.is_active !== false,
          status: values.status as string | undefined,
        });
      }

      let definitionId = String(values.component_definition_id || '');
      if (needDefinition || !definitionId) {
        const code = String(values.def_code || '')
          .trim()
          .toUpperCase();
        const name = String(values.def_name || '').trim();
        if (!code || !name) throw new Error('Definition code and name are required');
        const created = await pmvService.createComponentDefinition({
          family: String(values.family || family),
          code,
          name,
          brand: (values.def_brand as string) || null,
          model: (values.def_model as string) || null,
          specification_json:
            values.family === 'TYRE' && values.size_code
              ? { size_code: String(values.size_code) }
              : values.family === 'BATTERY' && values.nominal_voltage
                ? { nominal_voltage: Number(values.nominal_voltage) }
                : null,
        });
        definitionId = created.id;
        void qc.invalidateQueries({ queryKey: ['pmv-component-definitions'] });
      }

      return pmvService.createComponent({
        component_definition_id: definitionId,
        serial_number: (values.serial_number as string) || null,
        manufacturer_serial: (values.manufacturer_serial as string) || null,
        status: (values.status as string) || 'AVAILABLE',
        condition_code: (values.condition_code as string) || null,
        current_location_type: (values.current_location_type as string) || 'WAREHOUSE',
        current_location_reference: (values.current_location_reference as string) || null,
        ownership: (values.ownership as string) || null,
        provenance: 'REGISTERED',
        data_confidence: 'VERIFIED',
      });
    },
    onSuccess: (row) => {
      message.success(mode === 'edit' ? 'Component updated' : 'Component registered');
      void qc.invalidateQueries({ queryKey: ['pmv-component-register'] });
      void qc.invalidateQueries({ queryKey: ['pmv-components-available'] });
      void qc.invalidateQueries({ queryKey: ['pmv-component-passport'] });
      onClose();
      const id = String((row as { id?: string }).id || edit?.id || '');
      if (id) onSaved?.(id);
    },
    onError: (e: unknown) => {
      const ax = e as { message?: string; response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || ax.message || 'Save failed');
    },
  });

  return (
    <Modal
      title={
        mode === 'edit'
          ? `Edit ${edit?.component_number || 'component'}`
          : 'Register component'
      }
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={mode === 'edit' ? 'Save' : 'Register'}
      confirmLoading={saveMut.isPending}
      destroyOnClose
      width={560}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Physical component identity"
        description="Creates a lifecycle instance (TYR-/BAT- number). Inventory Item is product master only — stock qty is unchanged here."
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => saveMut.mutate(values)}
        onValuesChange={(changed) => {
          if (changed.family) setFamily(String(changed.family));
        }}
      >
        {mode === 'create' ? (
          <>
            <Form.Item name="family" label="Family" rules={[{ required: true }]}>
              <Select options={FAMILY_OPTS} />
            </Form.Item>
            {!needDefinition ? (
              <Form.Item
                name="component_definition_id"
                label="Specification / definition"
                rules={[{ required: !needDefinition, message: 'Select a definition' }]}
                extra={
                  <Button type="link" style={{ padding: 0 }} onClick={() => setNeedDefinition(true)}>
                    Or create a new definition
                  </Button>
                }
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={defOptions}
                  placeholder={defsQ.isLoading ? 'Loading…' : 'Select definition'}
                  notFoundContent="No definitions — create one below"
                />
              </Form.Item>
            ) : (
              <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
                <Text strong>New definition</Text>
                <Form.Item name="def_code" label="Code" rules={[{ required: true }]}>
                  <Input placeholder={family === 'TYRE' ? 'TYR-11R22.5' : 'BAT-12V'} />
                </Form.Item>
                <Form.Item name="def_name" label="Name" rules={[{ required: true }]}>
                  <Input placeholder="Display name" />
                </Form.Item>
                <Space wrap style={{ width: '100%' }}>
                  <Form.Item name="def_brand" label="Brand">
                    <Input style={{ width: 140 }} />
                  </Form.Item>
                  <Form.Item name="def_model" label="Model">
                    <Input style={{ width: 140 }} />
                  </Form.Item>
                  {family === 'TYRE' ? (
                    <Form.Item name="size_code" label="Size code">
                      <Input placeholder="11R22.5" style={{ width: 120 }} />
                    </Form.Item>
                  ) : null}
                  {family === 'BATTERY' ? (
                    <Form.Item name="nominal_voltage" label="Nominal V">
                      <Input placeholder="12" style={{ width: 80 }} />
                    </Form.Item>
                  ) : null}
                </Space>
                {defOptions.length > 0 ? (
                  <Button type="link" style={{ padding: 0 }} onClick={() => setNeedDefinition(false)}>
                    Use existing definition instead
                  </Button>
                ) : null}
              </Space>
            )}
          </>
        ) : null}

        <Form.Item name="serial_number" label="Serial number">
          <Input placeholder="Optional unique serial" />
        </Form.Item>
        <Form.Item name="manufacturer_serial" label="Manufacturer serial">
          <Input />
        </Form.Item>
        <Form.Item name="condition_code" label="Condition">
          <Select allowClear options={CONDITION_OPTS} />
        </Form.Item>
        {mode === 'create' ? (
          <Form.Item name="status" label="Status" initialValue="AVAILABLE">
            <Select
              options={[
                { value: 'AVAILABLE', label: 'Available' },
                { value: 'IN_STOCK', label: 'In stock' },
              ]}
            />
          </Form.Item>
        ) : (
          <Form.Item name="is_active" label="Record status">
            <Select
              options={[
                { value: true, label: 'Active' },
                { value: false, label: 'Inactive (soft deactivate)' },
              ]}
            />
          </Form.Item>
        )}
        <Form.Item name="current_location_type" label="Location type">
          <Select options={LOCATION_OPTS.filter((o) => o.value !== 'INSTALLED')} />
        </Form.Item>
        <Form.Item name="current_location_reference" label="Location reference">
          <Input placeholder="Warehouse / yard code" />
        </Form.Item>
        <Form.Item name="ownership" label="Ownership">
          <Input placeholder="Optional" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PmvComponentFormModal;
